const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function fetchAPI(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/ld+json',
    'Accept': 'application/ld+json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (response.status === 204) {
    return null;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData['hydra:description'] || errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export interface Course {
  '@id': string;
  id: number;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  estimatedDuration?: number;
  difficulty: string;
}

export interface LearningPathCourse {
  id: number;
  course: Course;
  sortOrder: number;
  isRequired: boolean;
  /** #519 — why the course is in the path (AI draft or author's note). */
  reason?: string | null;
}

export interface LearningPath {
  '@id': string;
  id: number;
  title: string;
  slug: string;
  description?: string;
  thumbnailUrl?: string;
  estimatedDuration?: number;
  difficulty: string;
  isActive: boolean;
  enrollmentMode?: 'none' | 'self' | 'request';
  allowedDepartments?: number[];
  allowedGroups?: number[];
  courses?: LearningPathCourse[];
  courseCount?: number;
  requiredCourseCount?: number;
  /** #519 — set on personal paths (only the owner sees them). */
  ownerUserId?: number | null;
  origin?: 'manual' | 'ai';
  goal?: string | null;
  targetLevel?: string | null;
  certificateMode?: 'per_course' | 'path';
}

export interface LearningPathEnrollment {
  '@id': string;
  id: number;
  learningPath: LearningPath;
  status: 'enrolled' | 'in_progress' | 'completed' | 'pending_approval' | 'rejected';
  progressPercentage: string;
  enrolledAt: string;
  startedAt?: string;
  completedAt?: string;
}

export interface LearningPathProgress {
  enrollment: LearningPathEnrollment;
  progress: number;
  completedCourses: number;
  totalCourses: number;
}

/**
 * Helper to extract members from API Platform response
 * Supports both API Platform 3.x (hydra:member) and 4.x (member) formats
 */
function getMembers(response: any): any[] {
  return response['hydra:member'] || response['member'] || [];
}

class LearningPathService {
  /**
   * Get all active learning paths
   */
  async getLearningPaths(): Promise<LearningPath[]> {
    const response = await fetchAPI(`${API_BASE_URL}/api/learning_paths?isActive=true`);
    return getMembers(response);
  }

  /**
   * Get a single learning path by ID
   */
  async getLearningPath(id: number): Promise<LearningPath> {
    return fetchAPI(`${API_BASE_URL}/api/learning_paths/${id}`);
  }

  /**
   * Get a learning path by slug
   */
  async getLearningPathBySlug(slug: string): Promise<LearningPath | null> {
    const response = await fetchAPI(`${API_BASE_URL}/api/learning_paths?slug=${encodeURIComponent(slug)}`);
    const paths = getMembers(response) || [];
    return paths.length > 0 ? paths[0] : null;
  }

  /**
   * Get courses in a learning path
   */
  async getPathCourses(pathId: number): Promise<LearningPathCourse[]> {
    const response = await fetchAPI(`${API_BASE_URL}/api/learning_path_courses?learningPath.id=${pathId}&order[sortOrder]=asc`);
    return getMembers(response) || [];
  }

  /**
   * Get all courses that can be assigned to a learning path (#408)
   */
  async getAvailableCourses(): Promise<Course[]> {
    const response = await fetchAPI(`${API_BASE_URL}/api/courses?ownedOnly=1&itemsPerPage=1000`);
    return getMembers(response) || [];
  }

  /**
   * Assign a course to a learning path (#408)
   */
  async addCourseToPath(pathId: number, courseIri: string, sortOrder: number): Promise<LearningPathCourse> {
    return fetchAPI(`${API_BASE_URL}/api/learning_path_courses`, {
      method: 'POST',
      body: JSON.stringify({
        learningPath: `/api/learning_paths/${pathId}`,
        course: courseIri,
        sortOrder,
      }),
    });
  }

  /**
   * Update an existing path-course row, e.g. its position (#408).
   * Reordering must always PATCH existing rows — never delete + recreate,
   * progress calculation joins over learning_path_course.
   */
  async updatePathCourse(id: number, changes: { sortOrder?: number; isRequired?: boolean }): Promise<LearningPathCourse> {
    return fetchAPI(`${API_BASE_URL}/api/learning_path_courses/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/merge-patch+json' },
      body: JSON.stringify(changes),
    });
  }

  /**
   * Remove a course from a learning path (#408)
   */
  async removeCourseFromPath(id: number): Promise<void> {
    await fetchAPI(`${API_BASE_URL}/api/learning_path_courses/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get current user's learning path enrollments
   */
  async getMyEnrollments(): Promise<LearningPathEnrollment[]> {
    // Use custom endpoint that recalculates progress on the fly
    const response = await fetchAPI(`${API_BASE_URL}/api/learning-paths/my-enrollments`);
    // This endpoint returns a plain array, not a Hydra collection
    return Array.isArray(response) ? response : getMembers(response) || [];
  }

  /**
   * Get active (non-completed) enrollments
   */
  async getActiveEnrollments(): Promise<LearningPathEnrollment[]> {
    const enrollments = await this.getMyEnrollments();
    return enrollments.filter(e => e.status !== 'completed');
  }

  /**
   * Get completed enrollments
   */
  async getCompletedEnrollments(): Promise<LearningPathEnrollment[]> {
    const enrollments = await this.getMyEnrollments();
    return enrollments.filter(e => e.status === 'completed');
  }

  /**
   * Get enrollment for a specific learning path
   */
  async getEnrollmentForPath(pathId: number): Promise<LearningPathEnrollment | null> {
    const response = await fetchAPI(`${API_BASE_URL}/api/learning_path_enrollments?learningPath.id=${pathId}`);
    const enrollments = getMembers(response) || [];
    return enrollments.length > 0 ? enrollments[0] : null;
  }

  /**
   * Enroll in a learning path
   */
  async enrollInPath(pathId: number): Promise<LearningPathEnrollment> {
    return fetchAPI(`${API_BASE_URL}/api/learning_path_enrollments`, {
      method: 'POST',
      body: JSON.stringify({
        learningPath: `/api/learning_paths/${pathId}`
      })
    });
  }

  /**
   * Unenroll from a learning path
   */
  async unenrollFromPath(enrollmentId: number): Promise<void> {
    await fetchAPI(`${API_BASE_URL}/api/learning_path_enrollments/${enrollmentId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Check if user is enrolled in a learning path
   */
  async isEnrolled(pathId: number): Promise<boolean> {
    const enrollment = await this.getEnrollmentForPath(pathId);
    return enrollment !== null;
  }

  /**
   * Get learning paths containing a specific course
   */
  async getPathsContainingCourse(courseId: number): Promise<LearningPath[]> {
    const response = await fetchAPI(`${API_BASE_URL}/api/learning_path_courses?course.id=${courseId}`);
    const pathCourses = getMembers(response) || [];

    // Extract unique learning paths
    const pathMap = new Map<number, LearningPath>();
    for (const pc of pathCourses) {
      if (pc.learningPath && !pathMap.has(pc.learningPath.id)) {
        pathMap.set(pc.learningPath.id, pc.learningPath);
      }
    }

    return Array.from(pathMap.values());
  }

  /**
   * Get learning paths by difficulty
   */
  async getLearningPathsByDifficulty(difficulty: string): Promise<LearningPath[]> {
    const response = await fetchAPI(`${API_BASE_URL}/api/learning_paths?isActive=true&difficulty=${encodeURIComponent(difficulty)}`);
    return getMembers(response) || [];
  }

  /**
   * Format progress percentage for display
   */
  formatProgress(progressPercentage: string | number): string {
    const value = typeof progressPercentage === 'string' ? parseFloat(progressPercentage) : progressPercentage;
    return `${Math.round(value)}%`;
  }

  /**
   * Format duration in minutes to human-readable string
   */
  formatDuration(minutes: number | undefined): string {
    if (!minutes) return '';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}min`;
  }

  /**
   * Get difficulty label
   */
  getDifficultyLabel(difficulty: string): string {
    const labels: Record<string, string> = {
      'beginner': 'Einstieg',
      'intermediate': 'Vertiefung',
      'advanced': 'Spezialisierung'
    };
    return labels[difficulty] || difficulty;
  }

  /**
   * Get status label
   */
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      'enrolled': 'Eingeschrieben',
      'in_progress': 'In Bearbeitung',
      'completed': 'Abgeschlossen',
      'pending_approval': 'Anfrage ausstehend',
      'rejected': 'Abgelehnt',
    };
    return labels[status] || status;
  }
}

export const learningPathService = new LearningPathService();
export default learningPathService;
